import React from "react";
import { Link } from "react-scroll";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import Window from "./Window";
import img from "../grad.PNG";

// One source-line of the hero "object" — rendered with a gutter number.
const Line = ({ n, indent = 0, children }) => (
  <div className="flex gap-4 whitespace-pre leading-7">
    <span className="gutter-col w-6 flex-shrink-0 select-none">{n}</span>
    <span className="min-w-0" style={{ paddingLeft: `${indent}ch` }}>
      {children}
    </span>
  </div>
);

const K = ({ children }) => <span className="text-syntax-prop">{children}</span>; // key
const S = ({ children }) => <span className="text-syntax-str">{children}</span>; // string
const P = ({ children }) => <span className="text-syntax-comment">{children}</span>; // punctuation

const Home = () => {
  return (
    <section
      name="home"
      className="relative flex min-h-screen w-full items-center overflow-hidden py-28"
    >
      <div className="animate-pulse-dot pointer-events-none absolute -left-40 top-16 h-[26rem] w-[26rem] rounded-full bg-glow-blue blur-2xl" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[26rem] w-[26rem] rounded-full bg-glow-purple blur-2xl" />

      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-8 px-4 md:grid-cols-[1.55fr_1fr]">
        {/* Editor pane */}
        <div className="animate-fade-up">
          <Window
            file="engineer.js"
            right={
              <span className="hidden items-center gap-1.5 text-xs sm:flex">
                <span className="h-1.5 w-1.5 rounded-full bg-term-green animate-pulse-dot" />
                <span className="text-term-green">building&nbsp;@&nbsp;JLR</span>
              </span>
            }
          >
            <div className="overflow-x-auto px-4 py-5 text-[0.9rem] sm:text-[0.95rem]">
              <Line n={1}>
                <span className="text-syntax-key">const</span>{" "}
                <span className="text-syntax-fn">engineer</span>{" "}
                <P>= {"{"}</P>
              </Line>
              <Line n={2} indent={2}>
                <K>name</K>
                <P>: </P>
                <S>"Khubayb Kapila"</S>
                <P>,</P>
              </Line>
              <Line n={3} indent={2}>
                <K>role</K>
                <P>: </P>
                <S>"Software Engineer"</S>
                <P>,</P>
              </Line>
              <Line n={4} indent={2}>
                <K>company</K>
                <P>: </P>
                <S>"JLR"</S>
                <P>,</P>
              </Line>
              <Line n={5} indent={2}>
                <K>location</K>
                <P>: </P>
                <S>"Coventry, UK"</S>
                <P>,</P>
              </Line>
              <Line n={6} indent={2}>
                <K>education</K>
                <P>: </P>
                <S>"1st Class BSc, Computer Science"</S>
                <P>,</P>
              </Line>
              <Line n={7} indent={2}>
                <K>focus</K>
                <P>: [</P>
                <S>"embedded"</S>
                <P>, </P>
                <S>"full-stack"</S>
                <P>, </P>
                <S>"automotive"</S>
                <P>],</P>
              </Line>
              <Line n={8} indent={2}>
                <K>mindset</K>
                <P>: </P>
                <S>"clean, reliable, well-tested"</S>
                <P>,</P>
              </Line>
              <Line n={9}>
                <P>{"}"};</P>
                <span className="ml-0.5 inline-block h-[1.15em] w-[0.55ch] translate-y-[0.2em] animate-blink bg-syntax-fn align-middle" />
              </Line>
            </div>

            {/* Status bar */}
            <div className="flex items-center gap-4 overflow-x-auto border-t border-editor-border bg-editor-elevated px-4 py-2 text-[0.7rem] text-syntax-comment">
              <span className="flex items-center gap-1.5 text-syntax-fn">
                <span className="text-gutter">⑂</span> main
              </span>
              <span>UTF-8</span>
              <span>JavaScript</span>
              <span className="ml-auto whitespace-nowrap">Ln 9, Col 3</span>
            </div>
          </Window>

          {/* Terminal-command CTAs */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="projects" smooth offset={-70} duration={500} className="btn-cmd btn-cmd-primary">
              <span className="text-editor-bg/70">$</span> view --projects
            </Link>
            <Link to="contact" smooth offset={-70} duration={500} className="btn-cmd">
              <span className="text-term-green">$</span> ./contact.sh
            </Link>

            <div className="ml-1 flex items-center gap-4 text-syntax-comment">
              <a href="https://github.com/khubaybk" target="_blank" rel="noreferrer" aria-label="GitHub" className="transition-colors hover:text-white">
                <FaGithub size={20} />
              </a>
              <a href="https://www.linkedin.com/in/khubayb-kapila-8ab85a156/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="transition-colors hover:text-white">
                <FaLinkedin size={20} />
              </a>
              <a href="mailto:kkhubayb@gmail.com" aria-label="Email" className="transition-colors hover:text-white">
                <HiOutlineMail size={22} />
              </a>
            </div>
          </div>
        </div>

        {/* Portrait — opened as an image preview tab */}
        <div className="animate-fade-up mx-auto w-60 sm:w-72 md:w-full md:max-w-xs" style={{ animationDelay: "150ms" }}>
          <Window file="khubayb.png" barClassName="text-xs">
            <div className="relative overflow-hidden">
              <img src={img} alt="Khubayb Kapila" className="w-full object-cover" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-editor-panel/70 via-transparent to-transparent" />
              <span className="animate-scanline pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-syntax-fn/10 to-transparent" />
            </div>
            <div className="flex items-center justify-between border-t border-editor-border bg-editor-elevated px-3 py-1.5 text-[0.68rem] text-syntax-comment">
              <span>1080 × 1080</span>
              <span className="text-term-green">● live</span>
            </div>
          </Window>
        </div>
      </div>
    </section>
  );
};

export default Home;
